import { z } from 'zod';

const id = z.string().regex(/^[A-Za-z0-9_-]{1,256}$/);
const timestamp = z.string().datetime({ offset: true });
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value =>
  Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value);

export const ZoomMeetingUuidSchema = z.string().regex(/^[A-Za-z0-9_+/=-]{1,256}$/);
export const ZoomDateWindowSchema = z.object({ from: date, to: date }).refine(value =>
  Date.parse(value.to) >= Date.parse(value.from) && Date.parse(value.to) - Date.parse(value.from) <= 27 * 86400000,
'Select at most 28 calendar dates');

export const ZoomRecordingFileSchema = z.object({
  id: id.optional(), file_type: z.string().min(1).max(64), file_extension: z.string().max(16).optional(),
  file_size: z.number().int().nonnegative().safe().optional(), status: z.string().max(64).optional(),
  recording_type: z.string().max(128).optional(), recording_start: timestamp.optional(), recording_end: timestamp.optional(),
}).refine(file => ['CC', 'TIMELINE'].includes(file.file_type) || Boolean(file.id && file.status && file.file_size !== undefined),
'Recording files require their native identity, status and size except for captions and timelines')
  .refine(file => !file.recording_start || !file.recording_end || Date.parse(file.recording_end) >= Date.parse(file.recording_start),
    'Recording end precedes its start');

export const ZoomRecordingSchema = z.object({
  account_id: id, host_id: id, uuid: ZoomMeetingUuidSchema,
  topic: z.string().max(4096), start_time: timestamp,
  duration: z.number().int().nonnegative(), recording_count: z.number().int().nonnegative().max(2000),
  recording_files: z.array(ZoomRecordingFileSchema).max(1000),
  participant_audio_files: z.array(ZoomRecordingFileSchema).max(1000).optional(),
}).superRefine((recording, ctx) => {
  const files = [...recording.recording_files, ...(recording.participant_audio_files ?? [])];
  const ids = files.flatMap(file => file.id ? [file.id] : []);
  if (recording.recording_count !== files.length || new Set(ids).size !== ids.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Recording file count or identities are inconsistent' });
  }
});

export type ZoomRecording = z.infer<typeof ZoomRecordingSchema>;

export const ZoomRecordingSelectionSchema = z.array(z.object({ meetingUuid: ZoomMeetingUuidSchema, fileId: id })).max(10)
  .refine(files => new Set(files.map(file => JSON.stringify([file.meetingUuid, file.fileId]))).size === files.length,
    'Select each recording file once');
export type ZoomRecordingSelection = z.infer<typeof ZoomRecordingSelectionSchema>;
